import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../stores/useAppStore'
import { apiEndpoints } from '../config/env'
import { jsaService } from '../services/jsaService'
import { offlineJsaService } from '../services/offlineJsaService'
import SignaturePad from './SignaturePad'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentCheckIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'

interface WorkOrderJSAData {
  workOrderJSAID?: number
  techID_FK: number
  workOrderID_FK: number
  jsaDate: string
  confirmation: boolean
  action: boolean
  jobStep1?: string
  hazard1?: string
  action1?: string
  who1?: string
  jobStep2?: string
  hazard2?: string
  action2?: string
  who2?: string
  jobStep3?: string
  hazard3?: string
  action3?: string
  who3?: string
  jsaSignature?: string
  technicianName?: string
}

interface WorkOrderJSAProps {
  workOrderId: number
  onBack: () => void
}

interface PropertyInfo {
  address: string
  technicianName: string
}

interface WorkOrderPhotoDto {
  workOrderPhotoID: number
  workOrderID_FK: number
  workOrderFile: string
  workOrderFile_thumb: string
  creationDate: string
  fullPath?: string
  thumbnailPath?: string
}

const standardJSARows = [
  {
    jobStep: "Assess entry into property",
    hazards: [
      "Unsafe access to entry",
      "Potentially dangerous animals", 
      "Unaccompanied minors on site",
      "Aggressive occupants"
    ],
    actions: [
      "Send entry notices inc safe access",
      "If animals are uncontrolled and appear aggressive DO NOT ENTER (call office)",
      "If unaccompanied minors on site DO NOT ENTER (call office)",
      "If aggressive occupants DO NOT ENTER (call office)"
    ],
    who: ["Admin Staff", "Electricians and Technicians"]
  },
  {
    jobStep: "Scope job",
    hazards: [
      "Slips/trip/falls",
      "Aggressive occupants"
    ],
    actions: [
      "Proper footwear",
      "Maintain awareness",
      "Exit property if feel unsafe"
    ],
    who: ["Electricians and Technicians"]
  },
  {
    jobStep: "Prepare for installation/maintenance",
    hazards: [
      "Bending and lifting whilst carrying tools/ladder inside",
      "Slips/trips/falls",
      "Working at heights",
      "Access manhole"
    ],
    actions: [
      "Use correct lifting techniques",
      "Proper footwear",
      "Clean and dry footwear",
      "Maintain 3 points of contact (on ladder)",
      "Ensure ladder is stable and setup correctly",
      "If someone home, let them know going into manhole and check in 10min",
      "If no one home, send Teams msg to admin team. Inform when out of manhole."
    ],
    who: ["Electricians and Technicians"]
  },
  {
    jobStep: "Complete 240V electrical work",
    hazards: [
      "Electrocution",
      "Heat illness",
      "Slips/trips/falls",
      "Rodents and reptiles in roofs"
    ],
    actions: [
      "Isolate switch board",
      "Confirm isolation by test",
      "Maintain hydration and awareness of heat illness signs",
      "Proper lighting (by using a torch or head torch) in roof",
      "Correct footwear",
      "Awareness",
      "Look for signs of reptiles-if feel unsafe get out"
    ],
    who: ["Electricians"],
    isElectrical: true
  }
]

const WorkOrderJSA: React.FC<WorkOrderJSAProps> = ({ workOrderId, onBack }) => {
  const { user, addNotification } = useAppStore()
  const queryClient = useQueryClient()
  
  const [jsaData, setJsaData] = useState<WorkOrderJSAData>(() => ({
    techID_FK: user?.technicianId || Number(user?.id) || 0,
    workOrderID_FK: workOrderId,
    jsaDate: new Date().toISOString().split('T')[0],
    confirmation: false,
    action: false,
    technicianName: user?.name || 'Unknown'
  }))
  
  const [signatureBase64, setSignatureBase64] = useState<string>('')
  const [showAdditionalHazards, setShowAdditionalHazards] = useState<boolean>(false)
  const [isCompleted, setIsCompleted] = useState(false)

  // Photo management state removed since photos section is disabled

  // Load work order details
  const { data: workOrderDetails, isLoading: loadingWorkOrder } = useQuery({
    queryKey: ['workorder-jsa-details', workOrderId],
    queryFn: async (): Promise<PropertyInfo> => {
      const response = await fetch(apiEndpoints.workOrders.details(workOrderId))
      if (!response.ok) {
        throw new Error('Failed to fetch work order details')
      }
      const data = await response.json()
      return {
        address: data.workOrder.propertyAddress || 'Unknown Property',
        technicianName: data.workOrder.technicianName || 'Unknown Technician'
      }
    }
  })

  // Load existing JSA data
  const { data: existingJSA, isLoading: loadingJSA } = useQuery({
    queryKey: ['workorder-jsa', workOrderId],
    queryFn: async (): Promise<WorkOrderJSAData | null> => {
      const response = await fetch(`${apiEndpoints.jsa}/workorder/${workOrderId}`)
      if (response.status === 404) {
        return null // No JSA exists yet
      }
      if (!response.ok) {
        throw new Error('Failed to fetch JSA data')
      }
      return response.json()
    },
    retry: false
  })

  // Handle existing JSA data when loaded
  useEffect(() => {
    if (existingJSA) {
      setJsaData(existingJSA)
      setShowAdditionalHazards(existingJSA.action)
      setIsCompleted(!!existingJSA.jsaSignature)
    }
  }, [existingJSA])

  // Update technician name from work order details
  useEffect(() => {
    if (workOrderDetails?.technicianName) {
      setJsaData(prev => ({ ...prev, technicianName: workOrderDetails.technicianName }))
    }
  }, [workOrderDetails])

  // Save JSA mutation (offline-first)
  const saveJSAMutation = useMutation({
    mutationFn: async (data: { jsaData: WorkOrderJSAData; signature?: string }) => {
      // Use offline-first JSA service
      const jsaPayload = {
        techID_FK: data.jsaData.techID_FK,
        workOrderID_FK: data.jsaData.workOrderID_FK,
        jsaDate: new Date(data.jsaData.jsaDate).toISOString(),
        confirmation: data.jsaData.confirmation,
        action: data.jsaData.action,
        jobStep1: data.jsaData.jobStep1 || '',
        hazard1: data.jsaData.hazard1 || '',
        action1: data.jsaData.action1 || '',
        who1: data.jsaData.who1 || '',
        jobStep2: data.jsaData.jobStep2 || '',
        hazard2: data.jsaData.hazard2 || '',
        action2: data.jsaData.action2 || '',
        who2: data.jsaData.who2 || '',
        jobStep3: data.jsaData.jobStep3 || '',
        hazard3: data.jsaData.hazard3 || '',
        action3: data.jsaData.action3 || '',
        who3: data.jsaData.who3 || '',
        jsaSignature: ''
      }

      return await offlineJsaService.saveWorkOrderJsa(jsaPayload, data.signature)
    },
    onSuccess: (result, variables) => {
      // Save signature to localStorage for future use
      if (variables.signature) {
        jsaService.saveSignatureToStorage(variables.signature)
      } else {
      }
      
      const isOffline = result?.offline || !navigator.onLine
      addNotification({
        type: 'success',
        message: isOffline 
          ? 'Work Order JSA saved offline! Will sync when connection is restored.'
          : 'Work Order JSA saved successfully!',
        timestamp: Date.now()
      })
      queryClient.invalidateQueries({ queryKey: ['workorder-jsa', workOrderId] })
      onBack()
    },
    onError: (error: Error) => {
      console.error('Failed to save JSA:', error)
      addNotification({
        type: 'error',
        message: error.message || 'Failed to save Work Order JSA. Please try again.',
        timestamp: Date.now()
      })
    }
  })

  // Handle form field changes
  const handleInputChange = (field: keyof WorkOrderJSAData, value: string | boolean) => {
    setJsaData(prev => ({ ...prev, [field]: value }))
  }

  // Handle action required change
  const handleActionChange = (value: boolean) => {
    setShowAdditionalHazards(value)
    handleInputChange('action', value)
  }

  // Handle signature change
  const handleSignatureChange = (signature: string) => {
    setSignatureBase64(signature)
  }

  // Photo handling functions are disabled for now
  // const handleViewJSAPhotos = async () => { ... }
  // const handleUploadJSAPhoto = () => { ... }
  // const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => { ... }

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!jsaData.confirmation) {
      addNotification({
        type: 'error',
        message: 'You must confirm that you have considered the JSA and implemented actions to minimise risks',
        timestamp: Date.now()
      })
      return
    }

    if (jsaData.action && (!jsaData.jobStep1 && !jsaData.hazard1 && !jsaData.action1 && !jsaData.who1)) {
      addNotification({
        type: 'error',
        message: 'You must fill in at least one action required row',
        timestamp: Date.now()
      })
      return
    }

    if (!signatureBase64 && !isCompleted) {
      addNotification({
        type: 'error',
        message: 'You must provide a signature to continue',
        timestamp: Date.now()
      })
      return
    }

    // Save JSA
    saveJSAMutation.mutate({
      jsaData: jsaData,
      signature: signatureBase64
    })
  }

  if (!user) {
    return (
      <div className="container-mobile py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Authentication Required</h3>
              <p className="text-gray-600 mt-1">Please log in to access the Work Order JSA form.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loadingWorkOrder || loadingJSA) {
    return (
      <div className="container-mobile py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading Work Order JSA...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={onBack}>
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <DocumentCheckIcon className="h-6 w-6 text-blue-600" />
                  <span>Work Order JSA</span>
                </CardTitle>
                {isCompleted && (
                  <div className="flex items-center space-x-2 mt-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">JSA Completed</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Offline Indicator */}
          {!navigator.onLine && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <div className="flex items-center">
                <span className="font-medium">📱 Offline Mode</span>
                <span className="ml-2">- JSA will be saved offline and synced when connection is restored</span>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div>
              <span className="text-sm text-gray-500">Property Address:</span>
              <p className="font-medium">{workOrderDetails?.address || 'Loading...'}</p>
            </div>
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
              Green is for 240V electrical work
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Standard JSA Table */}
      <Card>
        <CardHeader>
          <CardTitle>Standard Job Safety Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-semibold text-sm">Job step</th>
                  <th className="text-left p-3 font-semibold text-sm">Hazard/s in this step</th>
                  <th className="text-left p-3 font-semibold text-sm">What action are we going to take to minimise this risk</th>
                  <th className="text-left p-3 font-semibold text-sm">Who will take this action</th>
                </tr>
              </thead>
              <tbody>
                {standardJSARows.map((row, index) => (
                  <tr key={index} className={`border-b hover:bg-gray-50 ${row.isElectrical ? 'bg-green-50' : ''}`}>
                    <td className={`p-3 align-top ${row.isElectrical ? 'bg-green-100 font-medium' : ''}`}>
                      {row.jobStep}
                    </td>
                    <td className={`p-3 align-top ${row.isElectrical ? 'bg-green-100' : ''}`}>
                      <ol className="list-decimal list-inside space-y-1 text-sm">
                        {row.hazards.map((hazard, hIndex) => (
                          <li key={hIndex} className={row.isElectrical && hIndex === 3 ? 'font-medium' : ''}>
                            {hazard}
                          </li>
                        ))}
                      </ol>
                    </td>
                    <td className={`p-3 align-top ${row.isElectrical ? 'bg-green-100' : ''}`}>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {row.actions.map((action, aIndex) => (
                          <li key={aIndex} className={row.isElectrical && aIndex >= 5 ? 'font-medium' : ''}>
                            {action}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className={`p-3 align-top ${row.isElectrical ? 'bg-green-100 font-medium' : ''}`}>
                      <div className="space-y-1 text-sm">
                        {row.who.map((person, wIndex) => (
                          <div key={wIndex}>{person}</div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Questions */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          {/* Confirmation Statement */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Confirmation Statement - I have considered the above standard JSA for this job and have implemented the actions to minimise the risks?
            </Label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input 
                  type="radio" 
                  name="confirmation" 
                  value="true"
                  checked={jsaData.confirmation === true}
                  onChange={(e) => handleInputChange('confirmation', e.target.value === 'true')}
                  className="mr-2" 
                />
                Yes
              </label>
            </div>
          </div>

          {/* Action Required */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Action Required - Are there any additional hazards NOT included in the standard JSA?
            </Label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input 
                  type="radio" 
                  name="action" 
                  value="true"
                  checked={jsaData.action === true}
                  onChange={(e) => handleActionChange(e.target.value === 'true')}
                  className="mr-2" 
                />
                Yes
              </label>
              <label className="flex items-center">
                <input 
                  type="radio" 
                  name="action" 
                  value="false"
                  checked={jsaData.action === false}
                  onChange={(e) => handleActionChange(e.target.value === 'true')}
                  className="mr-2" 
                />
                No
              </label>
            </div>
          </div>

          {/* Additional Hazards Table */}
          {showAdditionalHazards && (
            <div className="mt-6">
              <h4 className="text-lg font-semibold mb-4">Additional Hazards</h4>
              <div className="overflow-x-auto">
                <table className="w-full table-auto border-collapse border">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-semibold text-sm border-r">Job step</th>
                      <th className="text-left p-3 font-semibold text-sm border-r">Hazard/s in this step</th>
                      <th className="text-left p-3 font-semibold text-sm border-r">What action are we going to take to minimise this risk</th>
                      <th className="text-left p-3 font-semibold text-sm">Who will take this action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map((num) => (
                      <tr key={num} className="border-b">
                        <td className="p-2 border-r">
                          <Textarea 
                            value={jsaData[`jobStep${num}` as keyof WorkOrderJSAData] as string || ''}
                            onChange={(e) => handleInputChange(`jobStep${num}` as keyof WorkOrderJSAData, e.target.value)}
                            className="w-full min-h-[60px] resize-none"
                            placeholder="Enter job step..."
                          />
                        </td>
                        <td className="p-2 border-r">
                          <Textarea 
                            value={jsaData[`hazard${num}` as keyof WorkOrderJSAData] as string || ''}
                            onChange={(e) => handleInputChange(`hazard${num}` as keyof WorkOrderJSAData, e.target.value)}
                            className="w-full min-h-[60px] resize-none"
                            placeholder="Enter hazards..."
                          />
                        </td>
                        <td className="p-2 border-r">
                          <Textarea 
                            value={jsaData[`action${num}` as keyof WorkOrderJSAData] as string || ''}
                            onChange={(e) => handleInputChange(`action${num}` as keyof WorkOrderJSAData, e.target.value)}
                            className="w-full min-h-[60px] resize-none"
                            placeholder="Enter actions..."
                          />
                        </td>
                        <td className="p-2">
                          <Textarea 
                            value={jsaData[`who${num}` as keyof WorkOrderJSAData] as string || ''}
                            onChange={(e) => handleInputChange(`who${num}` as keyof WorkOrderJSAData, e.target.value)}
                            className="w-full min-h-[60px] resize-none"
                            placeholder="Enter who..."
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photos Section */}
      {/* <Card>
        <CardHeader>
          <CardTitle>JSA Photos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={handleViewJSAPhotos}
              disabled={isLoadingPhotos}
              className="flex items-center space-x-2"
            >
              <EyeIcon className="h-4 w-4" />
              <span>{isLoadingPhotos ? 'Loading...' : 'View Photos'}</span>
            </Button>
            <Button 
              variant="outline" 
              onClick={handleUploadJSAPhoto}
              className="flex items-center space-x-2"
            >
              <CameraIcon className="h-4 w-4" />
              <span>Upload Photo</span>
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Upload photos to document hazards, conditions, or completed work related to this JSA.
          </p>
        </CardContent>
      </Card> */}

      {/* Signature Section */}
      <Card>
        <CardHeader>
          <CardTitle>Signature</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Completed By:</Label>
              <Input 
                value={jsaData.technicianName || ''}
                onChange={(e) => handleInputChange('technicianName', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date:</Label>
              <Input 
                value={new Date(jsaData.jsaDate).toLocaleDateString('en-AU')}
                disabled
                className="mt-1"
              />
            </div>
          </div>

          {!isCompleted ? (
            <div>
              <Label className="block mb-2">Digital Signature:</Label>
              <SignaturePad 
                onSignatureChange={handleSignatureChange}
                className="border rounded-lg"
                initialSignature={(() => {
                  const stored = jsaService.getSignatureFromStorage()
                  return stored
                })()}
              />
            </div>
          ) : (
            <div>
              <Label className="block mb-2">Digital Signature:</Label>
              {jsaData.jsaSignature ? (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img 
                    src={apiEndpoints.files.serve(jsaData.jsaSignature)} 
                    alt="JSA Signature" 
                    className="max-w-full h-auto border border-gray-200 rounded"
                  />
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-gray-50 text-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">JSA has been completed and signed</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={onBack}>
              Back to Work Order
            </Button>
            {!isCompleted && (
              <Button 
                onClick={handleSubmit}
                disabled={saveJSAMutation.isPending}
                className="px-8"
              >
                {saveJSAMutation.isPending ? 'Saving...' : 'Save & Submit JSA'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photos section removed since it's disabled */}
    </div>
  )
}

export default WorkOrderJSA 